'use client';

import { useState, useRef, useEffect } from 'react';

export default function AIAsistent() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', text: 'Salut! Sunt asistentul tău Adi-Mag. Cu ce te pot ajuta astăzi? Îmi poți cere recomandări de produse sau pachete complete!' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                scrollToBottom();
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const handleSend = async () => {
        if (!input.trim() || loading) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
        setLoading(true);

        try {
            const res = await fetch('/api/ai-assistant', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userMessage,
                    history: messages.map(m => ({ role: m.role, text: m.text }))
                }),
            });

            const data = await res.json();

            if (data.reply) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    text: data.reply,
                    products: data.products,
                    isPackage: data.isPackage
                }]);
            } else {
                throw new Error('No reply');
            }
        } catch (error) {
            setMessages(prev => [...prev, { role: 'assistant', text: 'Îmi pare rău, am întâmpinat o eroare. Te rog să încerci din nou.' }]);
        } finally {
            setLoading(false);
        }
    };

    const formatMessage = (text) => {
        if (!text) return '';

        let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        formatted = formatted.replace(/^(\d+\.|-)\s+/gm, '<br/>• ');

        formatted = formatted.replace(/\n/g, '<br/>');

        return formatted;
    };

    return (
        <>
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="ai-fab"
                    aria-label="Deschide Asistent AI"
                >
                    <div className="ai-launcher-status">
                        <span className="ai-online-dot"></span>
                    </div>
                    <span className="ai-fab-text">Asistent AI</span>
                </button>
            )}

            {isOpen && (
                <div className="ai-window">
                    <div className="ai-header">
                        <div className="ai-header-info">
                            <div className="ai-avatar-container">
                                <img src="/images/ai-avatar.png" alt="AI Avatar" className="ai-avatar-img" />
                                <span className="ai-avatar-status-dot"></span>
                            </div>
                            <div>
                                <div className="ai-title">Adi-Mag AI</div>
                                <div className="ai-status">Online · Răspunsuri despre magazin</div>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="ai-close">✕</button>
                    </div>

                    <div className="ai-messages">
                        {messages.map((msg, i) => (
                            <div key={i} className={`ai-msg-wrapper ai-msg-${msg.role}`}>
                                <div
                                    className={`ai-bubble ${msg.isPackage ? 'ai-bubble-package' : ''}`}
                                    dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
                                />

                                {msg.products?.length > 0 && (
                                    <div className="ai-product-grid">
                                        {msg.products.map(p => (
                                            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="ai-product-card">
                                                <div className="ai-product-img">
                                                    <img src={p.image} alt={p.title} />
                                                </div>
                                                <div className="ai-product-details">
                                                    <div className="ai-product-name">{p.title}</div>
                                                    <div className="ai-product-price" dangerouslySetInnerHTML={{ __html: p.priceHtml }} />
                                                    <div className="ai-product-btn">Vezi produs</div>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {loading && (
                            <div className="ai-msg-wrapper ai-msg-assistant">
                                <div className="ai-bubble ai-typing">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="ai-footer">
                        <div className="ai-input-row">
                            <div className="ai-input-container">
                                <textarea
                                    ref={inputRef}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
                                    placeholder="Cu ce te pot ajuta?"
                                    rows={1}
                                />
                            </div>
                            <button className="ai-send-button" onClick={handleSend} disabled={!input.trim() || loading}>
                                Trimite
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx global>{`
                :root {
                    --ai-primary: #e31e24;
                    --ai-primary-dark: #c1171c;
                    --ai-bg: #ffffff;
                    --ai-text: #1a1a1a;
                    --ai-gray: #f4f4f7;
                }

                .ai-fab {
                    position: fixed;
                    bottom: 30px;
                    right: 30px;
                    z-index: 9999;
                    background: var(--ai-primary);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    padding: 12px 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }

                .ai-fab:hover {
                    transform: translateY(-2px);
                }

                .ai-fab-icon {
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .ai-fab-text {
                    font-weight: 600;
                    font-size: 15px;
                }

                .ai-online-dot {
                    width: 10px;
                    height: 10px;
                    background: #22c55e;
                    border-radius: 50%;
                    display: block;
                    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.2);
                    animation: ai-pulse 2s infinite;
                }

                @keyframes ai-pulse {
                    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
                    70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
                    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
                }

                .ai-window {
                    position: fixed;
                    bottom: 30px;
                    right: 30px;
                    z-index: 99999;
                    width: 400px;
                    max-width: calc(100vw - 60px);
                    height: 600px;
                    max-height: calc(100vh - 60px);
                    background: var(--ai-bg);
                    border-radius: 12px;
                    border: 1px solid #eee;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    animation: ai-slide-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    font-family: 'Inter', sans-serif;
                }

                @keyframes ai-slide-in {
                    from { opacity: 0; transform: translateY(40px) scale(0.9); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }

                .ai-header {
                    background: var(--ai-primary);
                    color: white;
                    padding: 15px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .ai-header-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .ai-avatar-container {
                    position: relative;
                    width: 45px;
                    height: 45px;
                    background: #fff;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 2px;
                }

                .ai-avatar-img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    border-radius: 8px;
                }

                .ai-avatar-status-dot {
                    position: absolute;
                    bottom: -2px;
                    right: -2px;
                    width: 12px;
                    height: 12px;
                    background: #22c55e;
                    border: 2px solid var(--ai-primary);
                    border-radius: 50%;
                }

                .ai-title {
                    font-weight: 700;
                    font-size: 16px;
                }

                .ai-status {
                    font-size: 12px;
                    opacity: 0.9;
                }

                .ai-close {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 20px;
                    cursor: pointer;
                    opacity: 0.8;
                    transition: opacity 0.2s;
                }

                .ai-close:hover { opacity: 1; }

                .ai-messages {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                    background: #ffffff;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .ai-msg-wrapper {
                    display: flex;
                    flex-direction: column;
                    max-width: 85%;
                }

                .ai-msg-user { align-self: flex-end; }
                .ai-msg-assistant { align-self: flex-start; }

                .ai-bubble {
                    padding: 12px 16px;
                    border-radius: 18px;
                    font-size: 14px;
                    line-height: 1.5;
                }

                .ai-msg-user .ai-bubble {
                    background: var(--ai-primary);
                    color: white;
                    border-bottom-right-radius: 4px;
                }

                .ai-msg-assistant .ai-bubble {
                    background: white;
                    color: var(--ai-text);
                    border-bottom-left-radius: 4px;
                    border: 1px solid #eee;
                }

                .ai-bubble-package {
                    border: 2px solid var(--ai-primary);
                    background: #fff5f5 !important;
                }

                .ai-product-grid {
                    margin-top: 12px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    width: 100%;
                }

                .ai-product-card {
                    display: flex;
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    text-decoration: none;
                    color: inherit;
                    border: 1px solid #eee;
                    transition: transform 0.2s;
                }

                .ai-product-card:hover { transform: scale(1.02); }

                .ai-product-img {
                    width: 80px;
                    height: 80px;
                    flex-shrink: 0;
                    background: #f4f4f4;
                }

                .ai-product-img img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .ai-product-details {
                    padding: 10px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex: 1;
                    min-width: 0;
                }

                .ai-product-name {
                    font-size: 13px;
                    font-weight: 600;
                    margin-bottom: 4px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .ai-product-price {
                    font-size: 14px;
                    font-weight: 700;
                    color: var(--ai-primary);
                }

                .ai-product-price del { color: #999; font-size: 11px; margin-right: 5px; }

                .ai-product-btn {
                    margin-top: 4px;
                    font-size: 11px;
                    color: var(--ai-primary);
                    font-weight: 700;
                    text-transform: uppercase;
                }

                .ai-typing {
                    display: flex;
                    gap: 4px;
                    padding: 15px 20px;
                }

                .ai-typing span {
                    width: 8px;
                    height: 8px;
                    background: #ccc;
                    border-radius: 50%;
                    animation: ai-bounce 1.4s infinite ease-in-out both;
                }

                .ai-typing span:nth-child(1) { animation-delay: -0.32s; }
                .ai-typing span:nth-child(2) { animation-delay: -0.16s; }

                @keyframes ai-bounce {
                    0%, 80%, 100% { transform: scale(0); }
                    40% { transform: scale(1.0); }
                }

                .ai-footer {
                    padding: 15px 20px;
                    background: white;
                    border-top: 1px solid #eee;
                }

                .ai-input-row {
                    display: flex;
                    align-items: flex-end;
                    gap: 10px;
                    margin-bottom: 8px;
                }

                .ai-input-container {
                    flex: 1;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 0 12px;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                }

                .ai-input-container:focus-within {
                    border-color: var(--ai-primary);
                    box-shadow: 0 0 0 3px rgba(227, 30, 36, 0.1);
                }

                .ai-input-container textarea {
                    width: 100%;
                    background: none;
                    border: none;
                    resize: none;
                    padding: 10px 0;
                    font-family: inherit;
                    font-size: 14px;
                    outline: none;
                    height: 42px;
                    max-height: 100px;
                    display: block;
                    line-height: 22px;
                }

                .ai-send-button {
                    background: var(--ai-primary);
                    color: white;
                    border: none;
                    height: 42px;
                    padding: 0 20px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 14px;
                    font-weight: 700;
                    flex-shrink: 0;
                }

                .ai-send-button:hover:not(:disabled) {
                    background: var(--ai-primary-dark);
                    transform: translateY(-1px);
                }

                .ai-input-container button:disabled { opacity: 0.3; }

                .ai-branding {
                    text-align: center;
                    font-size: 10px;
                    color: #aaa;
                    margin-top: 10px;
                }

                @media (max-width: 480px) {
                    .ai-window {
                        width: 100vw;
                        height: 100vh;
                        bottom: 0;
                        right: 0;
                        border-radius: 0;
                        max-width: 100vw;
                        max-height: 100vh;
                    }
                    .ai-fab { bottom: 20px; right: 20px; }
                }
            `}</style>
        </>
    );
}